// The MRZ checker's words, in the shape every language fills, and the
// explainer the checker shares with the generator.
//
// The English object is assembled from the same constants the English page
// prints ($lib/seo/mrz-explainer, $lib/seo/mrz-parser), so the source a
// translation is made from cannot say something the page does not. What is
// data rather than prose stays out of it: the specimen zones, the worked
// example's characters and digits, the positions and the check digits all come
// from the functions that compute them, in every language.
//
// The parser's own words are part of the copy because the checker parses on
// every keystroke in the browser: its notes, errors, warnings and table labels
// are built from what it finds as it finds it, so a page in another language
// hands it that language's words rather than translating its output.

import {
  CHECK_DIGIT_GAP,
  CHECK_DIGIT_STEPS,
  CHECKER_FAQ,
  COMPOSITE_DIGIT,
  EXPLAINER_LINKS,
  FIELD_POSITION_LABELS_EN,
  type FieldPositionLabels,
  MRZ_DEFINITION,
  type ToolQuestion,
} from "$lib/seo/mrz-explainer";
import { MRZ_PARSER_TEXT_EN, type MrzParserText } from "$lib/seo/mrz-parser";
import { fromLocale } from "../load";
import type { TranslatedLocale } from "../locales";

/** One of the pages the explainer sends a reader to, in the order of EXPLAINER_LINKS. */
export interface ExplainerLinkCopy {
  readonly label: string;
  readonly blurb: string;
}

/** The explanation below both MRZ tools: what a zone is and how its digits work. */
export interface MrzExplainerCopy {
  readonly definitionHeading: string;
  /** What a machine-readable zone is, in one paragraph of 40–55 words. */
  readonly definition: string;
  /** The five numbered steps that compute a check digit. */
  readonly steps: readonly string[];
  /** Said before the worked example's table; `number` is the specimen's document number. */
  readonly workedExampleLead: (number: string) => string;
  readonly workedTable: {
    readonly caption: string;
    readonly character: string;
    readonly value: string;
    readonly weight: string;
    readonly product: string;
    readonly sum: string;
  };
  readonly compositeHeading: string;
  readonly composite: string;
  readonly gapHeading: string;
  readonly gap: string;
  readonly faqHeading: string;
  readonly nextHeading: string;
  /** The three pages of EXPLAINER_LINKS, same order. */
  readonly links: readonly ExplainerLinkCopy[];
}

export interface MrzCheckerCopy {
  readonly title: string;
  /** At most 160 characters. */
  readonly description: string;
  readonly heading: string;
  readonly intro: string;
  readonly inputLabel: string;
  readonly placeholder: string;
  /** The button that loads the specimen's zone in one format, e.g. "TD3". */
  readonly loadExample: (format: string) => string;
  readonly clear: string;
  /** Printed before the detected layout's name. */
  readonly format: string;
  readonly allValid: (total: number) => string;
  /** `fields` is the failing checks' labels, already joined. */
  readonly failing: (count: number, total: number, fields: string) => string;
  readonly checksHeading: string;
  readonly checksTable: {
    readonly covers: string;
    readonly where: string;
    readonly printed: string;
    readonly computed: string;
    readonly result: string;
  };
  readonly valid: string;
  readonly invalid: string;
  readonly fieldsHeading: string;
  readonly fieldsTable: {
    readonly field: string;
    readonly value: string;
    readonly asPrinted: string;
    readonly where: string;
  };
  /** Everything the parser itself says: labels, positions, notes, errors, warnings. */
  readonly parser: MrzParserText;
  readonly positionsHeading: string;
  readonly positionsIntro: string;
  readonly positionsTable: {
    readonly caption: string;
    readonly field: string;
    readonly td3: string;
    readonly td2: string;
    readonly td1: string;
  };
  readonly positionLabels: FieldPositionLabels;
  readonly checkDigitHeading: string;
  /**
   * The sentence after the worked example, as HTML. `field` and `digit` arrive
   * already wrapped in their `<code>` elements, so a language can put them in
   * whatever order its sentence needs.
   */
  readonly workedExampleResult: (field: string, digit: string) => string;
  readonly faq: readonly ToolQuestion[];
  readonly generatorLink: ExplainerLinkCopy;
  readonly explainer: MrzExplainerCopy;
}

/** The explainer's English, shared by both tools' English copy. */
export const MRZ_EXPLAINER_EN: MrzExplainerCopy = {
  definitionHeading: "What a machine-readable zone is",
  definition: MRZ_DEFINITION,
  steps: CHECK_DIGIT_STEPS,
  // The English constant is this sentence with the specimen's number in it;
  // the function form lets another language put the number where it belongs.
  workedExampleLead: (number) =>
    `Worked through on the specimen's passport number ${number} – an invented ` +
    "document belonging to nobody:",
  workedTable: {
    caption:
      "The 7-3-1 weighting worked through, character by character, on a specimen document number",
    character: "Character",
    value: "Value",
    weight: "Weight",
    product: "Product",
    sum: "Sum, then modulo 10",
  },
  compositeHeading: "The composite digit",
  composite: COMPOSITE_DIGIT,
  gapHeading: "What the digits do not cover",
  gap: CHECK_DIGIT_GAP,
  faqHeading: "Questions people ask",
  nextHeading: "Where to go next",
  links: EXPLAINER_LINKS.map((link) => ({ label: link.label, blurb: link.blurb })),
};

export const MRZ_CHECKER_EN: MrzCheckerCopy = {
  title: "MRZ parser and check digit calculator – TD1, TD2 and TD3",
  description:
    "Paste a passport or ID card MRZ (TD1, TD2 or TD3): every field parsed and every ICAO 9303 check digit verified, in your browser. Nothing is uploaded.",
  heading: "MRZ parser and check digit calculator",
  intro:
    "Paste the machine-readable zone of a passport, identity card or visa – TD3, TD2 or TD1. The " +
    "format is detected, every field is read out, and every ICAO 9303 check digit is recomputed and " +
    "compared with the one printed. It all runs in your browser; nothing leaves the page.",
  inputLabel: "MRZ (two or three lines)",
  placeholder: "Paste the lines of the zone here",
  loadExample: (format) => `Load ${format} example`,
  clear: "Clear",
  format: "Format:",
  allValid: (total) => `All ${total} check digits are valid.`,
  failing: (count, total, fields) =>
    `${count} of ${total} check digits do not match: ${fields}. A mismatch almost always means ` +
    "one character of that field was misread or mistyped.",
  checksHeading: "Check digits",
  checksTable: {
    covers: "Covers",
    where: "Where",
    printed: "Printed",
    computed: "Computed",
    result: "Result",
  },
  valid: "valid",
  invalid: "invalid",
  fieldsHeading: "Parsed fields",
  fieldsTable: { field: "Field", value: "Value", asPrinted: "As printed", where: "Where" },
  parser: MRZ_PARSER_TEXT_EN,
  positionsHeading: "Where each field sits in TD1, TD2 and TD3",
  positionsIntro:
    "Positions are counted from 1 on each line, the way ICAO 9303 numbers them. Every check digit " +
    "sits right after the field it protects; the composite digit is the last character of its " +
    "line.",
  positionsTable: {
    caption: "Field and check-digit positions in the three MRZ layouts",
    field: "Field",
    td3: "TD3 (passport)",
    td2: "TD2",
    td1: "TD1 (ID card)",
  },
  positionLabels: FIELD_POSITION_LABELS_EN,
  checkDigitHeading: "How a check digit is calculated",
  workedExampleResult: (field, digit) =>
    `So the digit printed after ${field} in that zone is ${digit}. Load the TD3 example above ` +
    "and the parser recomputes the same number in front of you.",
  faq: CHECKER_FAQ,
  generatorLink: {
    label: "MRZ generator",
    blurb: "build a valid zone with correct check digits for a test fixture.",
  },
  explainer: MRZ_EXPLAINER_EN,
};

const modules = import.meta.glob("../locales/*/mrz-checker.ts");

export function loadMrzCheckerCopy(locale: TranslatedLocale): Promise<MrzCheckerCopy> {
  return fromLocale<MrzCheckerCopy>(modules, locale, "mrz-checker");
}
